import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import { Theme } from "@mui/material/styles";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Pagination from "@mui/material/Pagination";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import FilterListIcon from "@mui/icons-material/FilterList";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { Autocomplete, OutlinedInput } from "@mui/material";
import {
  vehicleExpenseApi,
  VehicleExpenseResponse,
  ExpenseListResponse,
} from "./apiService";
import { EXPENSE_TYPES } from "./components/types";
import {
  formatExpenseType,
  getExpenseTypeColor,
  truncateText,
  groupExpensesByVehicle,
  calculateVehicleTotal,
} from "./utils/expenseUtils";
import ExpenseDetailView from "./components/ExpenseDetailView";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  formatCurrency,
  formatDate,
  formatDateRange,
  formatVehicleNumber,
  getDateRangeForFilter,
  TimeFilter,
} from "../../utils/utils";
import DeleteConfirmationDialog from "../../components/DeleteConfirmationDialog";
import { useToast } from "../../components/Toast";
import NavigationHeader from "../../components/NavigationHeader";
import {
  commonTextFieldProps,
  smallButtonProp,
  tableCellStyles,
} from "../../utils/styles";
import TimePeriodFilter from "../../components/TimePeriodFilter";

interface NoDataViewProps {
  onAddClick: () => void;
}

const NoDataView = ({ onAddClick }: NoDataViewProps) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 8,
      px: 2,
      backgroundColor: (theme) => theme.palette.grey[50],
      borderRadius: 2,
    }}
  >
    <ReceiptLongIcon
      sx={{
        fontSize: 64,
        color: (theme) => theme.palette.grey[300],
        mb: 2,
      }}
    />
    <Typography
      variant="h6"
      component="h2"
      sx={{
        color: (theme) => theme.palette.text.primary,
        mb: 1,
        textAlign: "center",
      }}
    >
      No Expenses Yet
    </Typography>
    <Typography
      variant="body1"
      color="text.secondary"
      sx={{ mb: 3, textAlign: "center", maxWidth: 400 }}
    >
      Start tracking your vehicle expenses by adding your first expense record.
    </Typography>
    <Button
      variant="contained"
      startIcon={<AddIcon />}
      onClick={onAddClick}
      size="large"
    >
      Add First Expense
    </Button>
  </Box>
);

const NoFilteredDataView = ({
  onClearFilters,
}: {
  onClearFilters: () => void;
}) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      py: 8,
      px: 2,
      backgroundColor: (theme) => theme.palette.grey[50],
      borderRadius: 2,
    }}
  >
    <FilterListIcon
      sx={{
        fontSize: 64,
        color: (theme) => theme.palette.grey[300],
        mb: 2,
      }}
    />
    <Typography
      variant="h6"
      component="h2"
      sx={{
        color: (theme) => theme.palette.text.primary,
        mb: 1,
        textAlign: "center",
      }}
    >
      No Expenses Found
    </Typography>
    <Typography
      variant="body1"
      color="text.secondary"
      sx={{ mb: 3, textAlign: "center", maxWidth: 400 }}
    >
      No expenses match your current filters. Try adjusting your filter
      criteria.
    </Typography>
    <Button
      variant="outlined"
      startIcon={<FilterListIcon />}
      onClick={onClearFilters}
      size="large"
    >
      Clear Filters
    </Button>
  </Box>
);

const VehicleExpense = () => {
  const navigate = useNavigate();

  const [expenses, setExpenses] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });

  // Filters
  const [filters, setFilters] = useState<{
    vehicleNumber: string;
    expenseType: string;
    expenseDate: string;
    endDate: string;
    sortBy: string;
    sortOrder: "ASC" | "DESC";
    vehicleNumbers: string[];
    timeFilter: TimeFilter | string;
    customStartDate: string;
    customEndDate: string;
  }>({
    vehicleNumber: "",
    expenseType: "",
    expenseDate: "",
    endDate: "",
    sortBy: "expenseDate",
    sortOrder: "DESC" as "ASC" | "DESC",
    vehicleNumbers: [],
    timeFilter: "",
    customStartDate: "",
    customEndDate: "",
  });

  const hasActiveFilters = () => {
    return Object.values(filters).some(
      (value) =>
        value !== "" &&
        !["DESC", "ASC", "expenseDate", "amount", [""]].includes(value)
    );
  };

  const [vehicles, setVehicles] = useState<
    Array<{ id: string; vehicleNumber: string; acquisitionType: string }>
  >([]);

  // Detail view state
  const [selectedExpenseId, setSelectedExpenseId] = useState<string>("");
  const [detailViewOpen, setDetailViewOpen] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const { showToast } = useToast();
  const filtersReset = useRef(false);

  const fetchExpenses = async (page: number = 1) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        page,
        limit: pagination.limit,
        ...filters,
      };

      // Remove empty filters
      Object.keys(params).forEach((key) => {
        if (params[key as keyof typeof params] === "") {
          delete params[key as keyof typeof params];
        }
      });
      const response = await vehicleExpenseApi.getExpenses(params);
      setExpenses(response?.expenses?.groupedExpenses);
      setPagination(response?.expenses?.pagination);
    } catch (err) {
      setError("Failed to fetch expenses. Please try again.");
      console.error("Error fetching expenses:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicles = async () => {
    try {
      const vehicleData = await vehicleExpenseApi.fetchVehicles();
      setVehicles(vehicleData);
    } catch (err) {
      console.error("Error fetching vehicles:", err);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  useEffect(() => {
    fetchExpenses(1);
  }, []);

  useEffect(() => {
    if (filtersReset.current) {
      fetchExpenses(1);
      filtersReset.current = false;
    }
  }, [filters]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    fetchExpenses(value);
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    filtersReset.current = true;
    setFilters({
      vehicleNumber: "",
      expenseType: "",
      expenseDate: "",
      endDate: "",
      sortBy: "expenseDate",
      sortOrder: "DESC",
      vehicleNumbers: [],
      timeFilter: "",
      customStartDate: "",
      customEndDate: "",
    });
  };

  const handleView = (expenseId: string) => {
    setSelectedExpenseId(expenseId);
    setDetailViewOpen(true);
  };

  const handleEdit = (expenseId: string) => {
    navigate(`/vehicle-expense/edit/${expenseId}`);
  };

  const handleCloseDetailView = () => {
    setDetailViewOpen(false);
    setSelectedExpenseId("");
  };

  const handleDelete = () => {
    if (selectedExpenseId) {
      vehicleExpenseApi.deleteExpense(selectedExpenseId).then((res) => {
        if (res) {
          fetchExpenses(1);
          setDeleteModalOpen(false);
          setSelectedExpenseId("");
          showToast("Expense deleted successfully", "success");
        } else {
          showToast("Failed to delete expense", "error");
          setDeleteModalOpen(false);
          setSelectedExpenseId("");
        }
      });
    }
  };

  // Group expenses by vehicle (with safety check)
  const expensesByVehicle = groupExpensesByVehicle(expenses || []);

  // Get option label
  const getOptionLabel = (option: any) => {
    if (typeof option === "string") return option;
    return option.vehicleNumber;
  };

  // Render vehicle option
  const renderVehicleOption = (props: any, option: any) => (
    <Box component="li" {...props}>
      <Box sx={{ width: "100%" }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
          {option.vehicleNumber}
        </Typography>
      </Box>
    </Box>
  );
  const [vehicleSearchText, setVehicleSearchText] = useState<string>("");

  const handleVehicleChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    setFilters((prev) => ({
      ...prev,
      vehicleNumbers: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const filteredVehicles = useMemo(
    () =>
      vehicles.filter((vehicle) =>
        vehicle.vehicleNumber
          .toLowerCase()
          .includes(vehicleSearchText.toLowerCase())
      ),
    [vehicles, vehicleSearchText]
  );

  const applyFilters = () => {
    fetchExpenses(1);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <NavigationHeader
        title="Vehicle Expense"
        button={{
          label: "Add Expense",
          onClick: () => navigate("/vehicle-expense/add"),
          icon: <AddIcon />,
        }}
        backNavigationEnabled={false}
      />

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 1.5, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small" sx={{ fontSize: "12px" }}>
              <InputLabel sx={{ fontSize: "12px" }}>Vehicles</InputLabel>
              <Select
                multiple
                value={filters.vehicleNumbers as any}
                label="Vehicles"
                onChange={handleVehicleChange}
                sx={{ fontSize: "12px" }}
                input={<OutlinedInput label="Vehicles" />}
                onOpen={() => setVehicleSearchText("")}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {`${selected.length} vehicle${
                      selected.length !== 1 ? "s" : ""
                    } selected`}
                  </Box>
                )}
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                }}
              >
                <MenuItem dense sx={{ p: 0, borderBottom: "1px solid #eee" }}>
                  <TextField
                    size="small"
                    placeholder="Search vehicles number"
                    value={vehicleSearchText}
                    onChange={(e) => setVehicleSearchText(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                  />
                </MenuItem>
                {filteredVehicles.map((vehicle) => (
                  <MenuItem
                    key={vehicle?.id}
                    value={vehicle?.id}
                    sx={{
                      fontSize: "12px",
                      "&.Mui-selected": {
                        backgroundColor: "rgba(13, 76, 139, 0.12)",
                      },
                      "&.Mui-selected:hover": {
                        backgroundColor: "rgba(13, 76, 139, 0.12)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                        {vehicle?.vehicleNumber}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "0.55rem",
                          color: "text.secondary",
                          backgroundColor: "grey.100",
                          px: 1,
                          py: 0.25,
                          borderRadius: 0.5,
                        }}
                      >
                        {vehicle?.acquisitionType}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small" sx={{ fontSize: "12px" }}>
              <InputLabel sx={{ fontSize: "12px" }}>Expense Type</InputLabel>
              <Select
                value={filters.expenseType}
                label="Expense Type"
                onChange={(e: SelectChangeEvent<string>) =>
                  handleFilterChange("expenseType", e.target.value)
                }
                sx={{ fontSize: "12px" }}
              >
                <MenuItem value="" sx={{ fontSize: "12px" }}>
                  All Types
                </MenuItem>
                {EXPENSE_TYPES.map((type) => (
                  <MenuItem key={type} value={type} sx={{ fontSize: "12px" }}>
                    {formatExpenseType(type)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TimePeriodFilter
              timeFilter={filters.timeFilter}
              onChange={(value) => handleFilterChange("timeFilter", value)}
            />
          </Grid>
          {filters.timeFilter === "custom" && (
            <>
              <Grid item xs={12} sm={6} md={1.5}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start Date"
                  value={filters.customStartDate}
                  onChange={(e) =>
                    handleFilterChange("customStartDate", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ style: { fontSize: "12px" } }}
                  {...commonTextFieldProps}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={1.5}>
                <TextField
                  fullWidth
                  type="date"
                  label="End Date"
                  value={filters.customEndDate}
                  onChange={(e) =>
                    handleFilterChange("customEndDate", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ style: { fontSize: "12px" } }}
                  {...commonTextFieldProps}
                />
              </Grid>
            </>
          )}
          <Grid
            item
            xs={12}
            md="auto"
            sx={{ marginLeft: "auto", display: "flex", gap: 1 }}
          >
            <Button
              {...smallButtonProp}
              variant="outlined"
              onClick={applyFilters}
              size="small"
            >
              Apply
            </Button>
            <Button
              variant="outlined"
              onClick={clearFilters}
              size="small"
              sx={{ px: 1, py: 0.5, fontSize: "12px" }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : !expenses || Object.keys(expenses || {}).length === 0 ? (
        hasActiveFilters() ? (
          <NoFilteredDataView onClearFilters={clearFilters} />
        ) : (
          <NoDataView onAddClick={() => navigate("/vehicle-expense/add")} />
        )
      ) : (
        <>
          <Paper sx={{ p: 1 }}>
            <TableContainer>
              <Table
                size="small"
                sx={{
                  "& .MuiTableCell-root": { py: 0.5, px: 0.5 },
                  "& .MuiTableCell-root:nth-of-type(1)": { width: "10%" }, // Vehicle
                  "& .MuiTableCell-root:nth-of-type(2)": { width: "15%" }, // Type
                  "& .MuiTableCell-root:nth-of-type(3)": { width: "20%" }, // Location
                  "& .MuiTableCell-root:nth-of-type(4)": { width: "12%" }, // Amount
                  "& .MuiTableCell-root:nth-of-type(5)": { width: "12%" }, // Balance
                  "& .MuiTableCell-root:nth-of-type(6)": { width: "12%" }, // Paid
                  "& .MuiTableCell-root:nth-of-type(7)": { width: "15%" }, // Paid To
                  "& .MuiTableCell-root:nth-of-type(8)": { width: "4%" }, // Actions
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        fontWeight: 600,
                        textAlign: "left",
                      }}
                    >
                      Vehicle
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        fontWeight: 600,
                        textAlign: "left",
                      }}
                    >
                      Type
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        fontWeight: 600,
                        textAlign: "left",
                      }}
                    >
                      Location
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        fontWeight: 600,
                        textAlign: "left",
                      }}
                    >
                      Amount
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        fontWeight: 600,
                        textAlign: "left",
                      }}
                    >
                      Balance
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        fontWeight: 600,
                        textAlign: "left",
                      }}
                    >
                      Paid
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        fontWeight: 600,
                        textAlign: "left",
                      }}
                    >
                      Paid To
                    </TableCell>
                    <TableCell
                      sx={{
                        ...tableCellStyles,
                        fontWeight: 600,
                        textAlign: "center",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(expenses || {}).length > 0 ? (
                    Object.entries(expenses || {}).map(
                      ([date, expensesList]: [string, any]) => {
                        if (!Array.isArray(expensesList)) return null;

                        return (
                          <React.Fragment key={date}>
                            {/* Date Header Row */}
                            <TableRow
                              sx={{
                                "& .MuiTableCell-root": {
                                  borderBottomWidth: "0px",
                                },
                              }}
                            >
                              <TableCell
                                colSpan={8}
                                sx={{
                                  // backgroundColor: "grey.50",
                                  py: 1,
                                  px: 2,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: "text.primary",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                  }}
                                >
                                  {formatDate(date)}
                                </Typography>
                              </TableCell>
                            </TableRow>

                            {/* Expense Rows for this date */}
                            {expensesList.map((expense: any, index) => (
                              <TableRow key={expense.id} hover>
                                <TableCell sx={{ ...tableCellStyles }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: "0.65rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {formatVehicleNumber(
                                      expense?.vehicle?.vehicleNumber
                                    ) || "-"}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ ...tableCellStyles }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: "0.65rem",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {formatExpenseType(expense?.expenseType)}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  sx={{ ...tableCellStyles, textAlign: "left" }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ fontSize: "0.65rem" }}
                                  >
                                    {expense?.location || "-"}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  sx={{ ...tableCellStyles, textAlign: "left" }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ fontSize: "0.65rem" }}
                                  >
                                    {formatCurrency(expense?.amount)}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  sx={{ ...tableCellStyles, textAlign: "left" }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ fontSize: "0.65rem" }}
                                  >
                                    {formatCurrency(expense?.balanceAmount)}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  sx={{ ...tableCellStyles, textAlign: "left" }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ fontSize: "0.65rem" }}
                                  >
                                    {formatCurrency(expense?.paidAmount)}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  sx={{ ...tableCellStyles, textAlign: "left" }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ fontSize: "0.65rem" }}
                                  >
                                    {expense?.paidTo || "-"}
                                  </Typography>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    ...tableCellStyles,
                                    textAlign: "center",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <IconButton
                                      onClick={() => handleView(expense.id)}
                                      title="View Expense"
                                      size="small"
                                      sx={{
                                        color: (theme) =>
                                          theme.palette.primary.main,
                                        padding: 0.5,
                                      }}
                                    >
                                      <VisibilityIcon
                                        sx={{ fontSize: "1rem" }}
                                      />
                                    </IconButton>
                                    <IconButton
                                      onClick={() => handleEdit(expense.id)}
                                      title="Edit Expense"
                                      size="small"
                                      sx={{
                                        color: (theme) =>
                                          theme.palette.primary.main,
                                        padding: 0.5,
                                      }}
                                    >
                                      <EditIcon sx={{ fontSize: "1rem" }} />
                                    </IconButton>
                                    <IconButton
                                      onClick={() => {
                                        setSelectedExpenseId(expense.id);
                                        setDeleteModalOpen(true);
                                      }}
                                      sx={{
                                        color: (theme) =>
                                          theme.palette.error.main,
                                        padding: 0.5,
                                      }}
                                      size="small"
                                      title="Delete Expense"
                                    >
                                      <DeleteIcon sx={{ fontSize: "1rem" }} />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </React.Fragment>
                        );
                      }
                    )
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            py: 4,
                          }}
                        >
                          <Typography
                            variant="h6"
                            color="text.secondary"
                            gutterBottom
                          >
                            No expenses available
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Create a new expense to get started
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {pagination?.totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </Paper>
        </>
      )}

      {/* Expense Detail View Dialog */}
      <ExpenseDetailView
        open={detailViewOpen}
        expenseId={selectedExpenseId}
        onClose={handleCloseDetailView}
        onEdit={handleEdit}
      />

      {/* Expense Delete Dialog */}
      <DeleteConfirmationDialog
        deleteModalOpen={deleteModalOpen}
        handleNo={() => setDeleteModalOpen(false)}
        handleYes={handleDelete}
        itemToDelete={selectedExpenseId}
        title="Delete Expense"
        message="Are you sure you want to delete this expense?"
      />
    </Container>
  );
};

export default VehicleExpense;
